import {
  XDomain,
  XScale,
  YDomain,
  YScale,
} from 'components/Charting/ChartBuilder';
import { CHART_CONSTANTS } from 'components/Charting/constants';
import { SVGGSelection } from 'components/Charting/types';
import { getYAxisRange } from 'components/Charting/utils';
import {
  add,
  differenceInHours,
  format,
  roundToNearestMinutes,
} from 'date-fns';

export interface AxesBlueprintUpdateContext {
  innerWidth: number;
  innerHeight: number;
  xDomain: XDomain;
  yDomain: YDomain;
  xScale: XScale;
  yScale: YScale;
  yUnitLabel: string;
}

const FIVE_MINUTES_MS = 5 * 1000 * 60;

export const isNowModeXDomain = (xDomain: XDomain) =>
  +xDomain[1] - +xDomain[0] <= FIVE_MINUTES_MS;

const getXTimeTicks = ({ xDomain }: { xDomain: XDomain }) => {
  if (isNowModeXDomain(xDomain)) {
    const first = roundToNearestMinutes(xDomain[0], {
      roundingMethod: 'floor',
    });

    return Array.from(
      { length: CHART_CONSTANTS.xAxisLabelsCount },
      (_: any, i: number) =>
        add(first, {
          seconds: i * CHART_CONSTANTS.xNowModeLabelSpacingSeconds,
        }),
    );
  }

  const hoursInDay = differenceInHours(xDomain[1], xDomain[0]);
  const offset = hoursInDay - CHART_CONSTANTS.hoursInStandardDay;

  const firstSegmentEnd = add(xDomain[0], {
    hours: CHART_CONSTANTS.xLabelSpacingHours + offset,
  });

  const remainingSegments = Array.from(
    { length: CHART_CONSTANTS.xAxisLabelsCount - 1 },
    (_: any, i: number) =>
      add(firstSegmentEnd, {
        hours: i * CHART_CONSTANTS.xLabelSpacingHours,
      }),
  );
  return [xDomain[0], firstSegmentEnd, ...remainingSegments];
};

const getYTicks = ({ yDomain }: { yDomain: YDomain }) =>
  getYAxisRange(yDomain[0], yDomain[1], CHART_CONSTANTS.yTicksCountPerSide * 2);

export interface AxesBlueprintBuilderContext {
  xAxisGridLinesG: SVGGSelection;
  yAxisGridLinesG: SVGGSelection;
  xAxisBottomG: SVGGSelection;
  yAxisLeftG: SVGGSelection;
  getFormattedYLabelValue: (value: number) => string;
}

export const getYAxisBlueprintUpdater = ({
  yAxisLeftG,
}: AxesBlueprintBuilderContext) => ({
  yAxis: (() => {
    yAxisLeftG.append('line').classed('y-axis', true);

    return ({
      xDomain,
      yDomain,
      xScale,
      yScale,
    }: AxesBlueprintUpdateContext) => {
      yAxisLeftG
        .select('.y-axis')
        .attr('x1', xScale(xDomain[0]))
        .attr('x2', xScale(xDomain[0]))
        .attr('y1', yScale(yDomain[0]))
        .attr('y2', yScale(yDomain[1]))
        .attr('stroke-width', 1)
        .attr('stroke', '#E4E7ED');
    };
  })(),
});

export const getXTimeLabelsBlueprintUpdater = ({
  xAxisBottomG,
}: AxesBlueprintBuilderContext) => ({
  xTimeLabels: (() => {
    for (let i = 0; i < CHART_CONSTANTS.xAxisLabelsCount; i++) {
      xAxisBottomG.append('text').classed('x-tick', true);
    }

    return ({ innerHeight, xDomain, xScale }: AxesBlueprintUpdateContext) => {
      const xTimeTicks = getXTimeTicks({ xDomain });
      const timeFormat = isNowModeXDomain(xDomain)
        ? CHART_CONSTANTS.xAxisNowModeTimeFormat
        : CHART_CONSTANTS.xAxisTimeFormat;

      xAxisBottomG
        .selectAll('.x-tick')
        .text((_: any, i: number) => format(xTimeTicks[i], timeFormat))
        .attr('x', (_: any, i: number) => xScale(xTimeTicks[i]))
        .attr('y', innerHeight + 30)
        .attr('text-anchor', 'middle')
        .attr('font-size', '12px');
    };
  })(),
});

export const getXGridLinesBlueprintUpdater = ({
  xAxisGridLinesG,
}: AxesBlueprintBuilderContext) => ({
  xGridLines: (() => {
    for (let i = 0; i < CHART_CONSTANTS.xAxisLabelsCount; i++) {
      xAxisGridLinesG.append('line').classed('x-grid-line', true);
    }

    return ({ innerHeight, xDomain, xScale }: AxesBlueprintUpdateContext) => {
      const xGridLines = getXTimeTicks({ xDomain });
      xAxisGridLinesG
        .selectAll('.x-grid-line')
        .attr('x1', (_: any, i: number) => xScale(xGridLines[i]))
        .attr('x2', (_: any, i: number) => xScale(xGridLines[i]))
        .attr('y1', -16)
        .attr('y2', innerHeight + 16);
    };
  })(),
});

export const getYLabelsBlueprintUpdater = ({
  yAxisLeftG,
  getFormattedYLabelValue,
}: AxesBlueprintBuilderContext) => ({
  yLabels: (() => {
    for (let i = 0; i < CHART_CONSTANTS.yTicksCountPerSide * 2 + 2; i++) {
      yAxisLeftG.append('text').classed('y-tick', true);
    }

    return ({
      xDomain,
      yDomain,
      xScale,
      yScale,
      yUnitLabel,
    }: AxesBlueprintUpdateContext) => {
      const yTicks = getYTicks({ yDomain });
      const [minX] = xDomain;

      yAxisLeftG
        .selectAll('.y-tick')
        .text((_: any, i: number) =>
          yTicks[i] !== undefined
            ? `${getFormattedYLabelValue(yTicks[i])} ${yUnitLabel}`
            : '',
        )
        .attr('x', xScale(minX) - 20)
        .attr('y', (_: any, i: number) => yScale(yTicks[i] ?? 0) + 5)
        .attr('text-anchor', 'end')
        .attr('font-size', '12px')
        .attr('letter-spacing', '-0.5px');
    };
  })(),
});

export const getYGridLinesBlueprintUpdater = ({
  yAxisGridLinesG,
  yAxisLeftG,
}: AxesBlueprintBuilderContext) => ({
  yGridLines: (() => {
    for (let i = 0; i < CHART_CONSTANTS.yGridLinesPerSide * 2 + 2; i++) {
      yAxisGridLinesG.append('line').classed('y-grid-line', true);
      yAxisLeftG.append('line').classed('y-grid-line', true);
    }

    return ({
      xDomain,
      yDomain,
      xScale,
      yScale,
    }: AxesBlueprintUpdateContext) => {
      const yGridLines = (() => {
        const [start, end] = yDomain;

        return getYAxisRange(start, end, CHART_CONSTANTS.yGridLinesPerSide);
      })();

      const isYZeroLine = (_: unknown, i: number) => {
        return yGridLines[i] === 0;
      };
      const isYHidden = (_: unknown, i: number) => {
        return typeof yGridLines[i] === 'undefined';
      };

      yAxisGridLinesG
        .selectAll('.y-grid-line')
        .attr('x1', xScale(xDomain[0]))
        .attr('x2', xScale(xDomain[1]) + 20)
        .attr('y1', (_: any, i: number) => yScale(yGridLines[i]))
        .attr('y2', (_: any, i: number) => yScale(yGridLines[i]))
        .classed('y-grid-line-zero-axis', isYZeroLine)
        .classed('y-grid-line-hidden', isYHidden);

      yAxisLeftG
        .selectAll('.y-grid-line')
        .attr('x1', xScale(xDomain[0]) - 16)
        .attr('x2', xScale(xDomain[0]))
        .attr('y1', (_: any, i: number) => yScale(yGridLines[i]))
        .attr('y2', (_: any, i: number) => yScale(yGridLines[i]))
        .classed('y-grid-line-zero-axis', isYZeroLine)
        .classed('y-grid-line-hidden', isYHidden);
    };
  })(),
});
